import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { auditService } from '../../services/auditService';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import Input from '../../components/ui/Input';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const AdvancedSystemAnalyticsAuditDashboard = () => {
  const navigate = useNavigate();
  const { userProfile, loading: authLoading, signOut } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // System stats
  const [systemStats, setSystemStats] = useState({
    totalReferrals: 0,
    totalPractitioners: 0,
    totalUsers: 0,
    totalAuditLogs: 0
  });
  
  // Audit logs
  const [auditLogs, setAuditLogs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  
  // Filters
  const [filters, setFilters] = useState({
    action: '',
    tableName: '',
    userId: '',
    startDate: '',
    endDate: ''
  });
  
  // Selection for bulk operations
  const [selectedLogs, setSelectedLogs] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Available filter options
  const [actionOptions, setActionOptions] = useState([]);
  const [tableOptions, setTableOptions] = useState([]);

  // Check super admin access
  useEffect(() => {
    if (!authLoading && userProfile?.role !== 'super_admin') {
      navigate('/admin-login', { replace: true });
    }
  }, [authLoading, userProfile, navigate]);

  // Load data
  useEffect(() => {
    if (!authLoading && userProfile?.role === 'super_admin') {
      loadDashboardData();
    }
  }, [authLoading, userProfile, currentPage, filters]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Load system statistics
      const [referralsCount, practitionersCount, usersCount, auditLogsCount] = await Promise.all([
        supabase?.from('referrals')?.select('*', { count: 'exact', head: true }),
        supabase?.from('practitioners')?.select('*', { count: 'exact', head: true }),
        supabase?.from('user_profiles')?.select('*', { count: 'exact', head: true }),
        supabase?.from('audit_logs')?.select('*', { count: 'exact', head: true })
      ]);
      
      setSystemStats({
        totalReferrals: referralsCount?.count || 0,
        totalPractitioners: practitionersCount?.count || 0,
        totalUsers: usersCount?.count || 0,
        totalAuditLogs: auditLogsCount?.count || 0
      });
      
      // Load unique actions and tables for filter dropdowns
      const { data: allLogs } = await supabase
        ?.from('audit_logs')
        ?.select('action, table_name')
        ?.order('created_at', { ascending: false })
        ?.limit(1000);
      
      if (allLogs) {
        const uniqueActions = [...new Set(allLogs?.map(log => log?.action)?.filter(Boolean))];
        const uniqueTables = [...new Set(allLogs?.map(log => log?.table_name)?.filter(Boolean))];
        setActionOptions(uniqueActions);
        setTableOptions(uniqueTables);
      }
      
      // Load audit logs with filters
      const result = await auditService?.getAuditLogs({
        page: currentPage,
        pageSize: pageSize,
        action: filters?.action || null,
        tableName: filters?.tableName || null,
        userId: filters?.userId || null,
        startDate: filters?.startDate || null,
        endDate: filters?.endDate || null
      });
      
      setAuditLogs(result?.logs || []);
      setTotalCount(result?.totalCount || 0);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
    setCurrentPage(1);
    setSelectedLogs([]);
    setSelectAll(false);
  };

  const handleClearFilters = () => {
    setFilters({
      action: '',
      tableName: '',
      userId: '',
      startDate: '',
      endDate: ''
    });
    setCurrentPage(1);
    setSelectedLogs([]);
    setSelectAll(false);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedLogs([]);
    } else {
      setSelectedLogs(auditLogs?.map(log => log?.id));
    }
    setSelectAll(!selectAll);
  };

  const handleSelectLog = (logId) => {
    if (selectedLogs?.includes(logId)) {
      setSelectedLogs(selectedLogs?.filter(id => id !== logId));
    } else {
      setSelectedLogs([...selectedLogs, logId]);
    }
  };

  const handleExportSelected = async () => {
    try {
      const logsToExport = selectedLogs?.length > 0 
        ? auditLogs?.filter(log => selectedLogs?.includes(log?.id))
        : auditLogs;
      
      if (logsToExport?.length === 0) {
        alert('Aucun log à exporter');
        return;
      }
      
      const csv = [
        ['Date', 'Action', 'Table', 'Utilisateur', 'Email', 'Rôle', 'ID Enregistrement']?.join(','),
        ...logsToExport?.map(log => [
          formatDate(log?.createdAt),
          log?.action || '-',
          log?.tableName || '-',
          log?.userId || '-',
          log?.userEmail || '-',
          log?.userRole || '-',
          log?.recordId || '-'
        ]?.join(','))
      ]?.join('\n');
      
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL?.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${format(new Date(), 'yyyy-MM-dd-HHmm')}.csv`;
      a?.click();
      window.URL?.revokeObjectURL(url);
      
      alert(`✅ ${logsToExport?.length} log(s) exporté(s) avec succès`);
    } catch (err) {
      console.error('Error exporting logs:', err);
      alert('❌ Erreur lors de l\'export');
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedLogs?.length === 0) {
      alert('Aucun log sélectionné');
      return;
    }

    const confirmed = window.confirm(
      `⚠️ Êtes-vous sûr de vouloir supprimer ${selectedLogs?.length} log(s) ?\n\nCette action est irréversible.`
    );

    if (!confirmed) return;

    try {
      const { error } = await supabase?.from('audit_logs')?.delete()?.in('id', selectedLogs);
      
      if (error) throw error;
      
      alert(`✅ ${selectedLogs?.length} log(s) supprimé(s) avec succès`);
      setSelectedLogs([]);
      setSelectAll(false);
      await loadDashboardData();
    } catch (err) {
      console.error('Error deleting logs:', err);
      alert('❌ Erreur lors de la suppression');
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  const getActionBadge = (action) => {
    const styles = {
      CREATE: 'bg-success-bg text-success border border-success/30',
      UPDATE: 'bg-blue-500/10 text-blue-400 border border-blue-500/30',
      DELETE: 'bg-error-bg text-error border border-error/30',
      LOGIN: 'bg-accent/10 text-accent border border-accent/30',
      LOGOUT: 'bg-card-elevated text-muted border border-white/8',
      SEND_CREDENTIALS_EMAIL: 'bg-green-500/10 text-green-400 border border-green-500/30'
    };
    
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${styles?.[action] || styles?.LOGOUT}`}>
        {action}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy HH:mm', { locale: fr });
    } catch {
      return '-';
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);
  const hasActiveFilters = filters?.action || filters?.tableName || filters?.userId || filters?.startDate || filters?.endDate;

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0F0F12] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto mb-4"></div>
          <p className="text-secondary">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Analyse Système & Audit - Super Admin</title>
      </Helmet>
      <div className="min-h-screen bg-[#0F0F12]">
        {/* Header */}
        <header className="sticky top-0 z-50 bg-card/95 backdrop-blur-xl border-b border-white/8 shadow-xl">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate('/super-admin-dashboard')}
                  className="p-2 hover:bg-card-hover rounded-lg transition-colors"
                >
                  <Icon name="ArrowLeft" size={20} className="text-secondary" />
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-primary">Analyse Système & Audit</h1>
                  <p className="text-sm text-muted mt-1">Surveillance et traçabilité complète</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="px-4 py-2 bg-success-bg border border-success/30 rounded-xl">
                  <p className="text-xs font-medium text-success">Système opérationnel</p>
                </div>
                <Button
                  onClick={handleSignOut}
                  className="flex items-center gap-2 px-4 py-2 text-error hover:bg-error-bg rounded-xl transition-colors"
                >
                  <Icon name="LogOut" size={18} />
                  <span className="text-sm font-medium">Déconnexion</span>
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-card rounded-2xl shadow-2xl border border-white/8 p-6">
              <div className="flex items-center justify-between mb-2">
                <Icon name="FileText" size={20} className="text-accent" />
                <span className="text-xs text-muted">Total</span>
              </div>
              <p className="text-3xl font-bold text-primary">{systemStats?.totalReferrals}</p>
              <p className="text-sm text-secondary mt-1">Adressages</p>
            </div>
            
            <div className="bg-card rounded-2xl shadow-2xl border border-white/8 p-6">
              <div className="flex items-center justify-between mb-2">
                <Icon name="Users" size={20} className="text-blue-400" />
                <span className="text-xs text-muted">Total</span>
              </div>
              <p className="text-3xl font-bold text-primary">{systemStats?.totalPractitioners}</p>
              <p className="text-sm text-secondary mt-1">Praticiens</p>
            </div>
            
            <div className="bg-card rounded-2xl shadow-2xl border border-white/8 p-6">
              <div className="flex items-center justify-between mb-2">
                <Icon name="UserCheck" size={20} className="text-success" />
                <span className="text-xs text-muted">Total</span>
              </div>
              <p className="text-3xl font-bold text-primary">{systemStats?.totalUsers}</p>
              <p className="text-sm text-secondary mt-1">Utilisateurs</p>
            </div>
            
            <div className="bg-card rounded-2xl shadow-2xl border border-white/8 p-6">
              <div className="flex items-center justify-between mb-2">
                <Icon name="Activity" size={20} className="text-purple-400" />
                <span className="text-xs text-muted">Total</span>
              </div>
              <p className="text-3xl font-bold text-primary">{systemStats?.totalAuditLogs}</p>
              <p className="text-sm text-secondary mt-1">Logs d'audit</p>
            </div>
          </div>

          {/* Filters Panel */}
          <div className="bg-card rounded-2xl shadow-2xl border border-white/8 p-6 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-primary flex items-center gap-2">
                <Icon name="Filter" size={20} className="text-accent" />
                Filtres Avancés
              </h3>
              {hasActiveFilters && (
                <Button
                  onClick={handleClearFilters}
                  className="text-sm text-error hover:bg-error-bg px-4 py-2 rounded-lg transition-colors"
                >
                  Réinitialiser les filtres
                </Button>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-primary mb-2">Action</label>
                <Select
                  value={filters?.action}
                  onChange={(e) => handleFilterChange('action', e?.target?.value)}
                >
                  <option value="">Toutes les actions</option>
                  {actionOptions?.map((action) => (
                    <option key={action} value={action}>{action}</option>
                  ))}
                </Select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-primary mb-2">Table</label>
                <Select
                  value={filters?.tableName}
                  onChange={(e) => handleFilterChange('tableName', e?.target?.value)}
                >
                  <option value="">Toutes les tables</option>
                  {tableOptions?.map((table) => (
                    <option key={table} value={table}>{table}</option>
                  ))}
                </Select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-primary mb-2">ID Utilisateur</label>
                <Input
                  type="text"
                  value={filters?.userId}
                  onChange={(e) => handleFilterChange('userId', e?.target?.value)}
                  placeholder="UUID utilisateur"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-primary mb-2">Date début</label>
                <Input
                  type="date"
                  value={filters?.startDate}
                  onChange={(e) => handleFilterChange('startDate', e?.target?.value)}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-primary mb-2">Date fin</label>
                <Input
                  type="date"
                  value={filters?.endDate}
                  onChange={(e) => handleFilterChange('endDate', e?.target?.value)}
                />
              </div>
            </div>
          </div>

          {/* Bulk Operations Bar */}
          {selectedLogs?.length > 0 && (
            <div className="bg-accent/10 border border-accent/30 rounded-2xl p-4 mb-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Icon name="CheckSquare" size={20} className="text-accent" />
                <span className="text-sm font-medium text-primary">
                  {selectedLogs?.length} log(s) sélectionné(s)
                </span>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  onClick={handleExportSelected}
                  className="flex items-center gap-2 px-4 py-2 bg-success hover:bg-success-hover text-white rounded-lg transition-colors"
                >
                  <Icon name="Download" size={16} />
                  <span className="text-sm font-medium">Exporter</span>
                </Button>
                <Button
                  onClick={handleDeleteSelected}
                  className="flex items-center gap-2 px-4 py-2 bg-error hover:bg-error-hover text-white rounded-lg transition-colors"
                >
                  <Icon name="Trash2" size={16} />
                  <span className="text-sm font-medium">Supprimer</span>
                </Button>
              </div>
            </div>
          )}

          {/* Audit Logs Table */}
          <div className="bg-card rounded-2xl shadow-2xl border border-white/8 overflow-hidden">
            <div className="p-6 border-b border-white/8 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-primary flex items-center gap-2">
                <Icon name="List" size={20} className="text-accent" />
                Logs d'Audit ({totalCount})
              </h3>
              <div className="flex items-center gap-3">
                <Button
                  onClick={handleExportSelected}
                  className="flex items-center gap-2 px-4 py-2 bg-card-elevated hover:bg-card-hover text-secondary rounded-lg transition-colors"
                >
                  <Icon name="Download" size={16} />
                  <span className="text-sm">Exporter tout</span>
                </Button>
              </div>
            </div>
            
            {loading ? (
              <div className="p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto mb-4"></div>
                <p className="text-secondary">Chargement des logs...</p>
              </div>
            ) : error ? (
              <div className="p-12 text-center">
                <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
                <p className="text-error">{error}</p>
              </div>
            ) : auditLogs?.length === 0 ? (
              <div className="p-12 text-center">
                <Icon name="Inbox" size={48} className="text-muted mx-auto mb-4" />
                <p className="text-secondary">Aucun log trouvé</p>
                {hasActiveFilters && (
                  <Button
                    onClick={handleClearFilters}
                    className="mt-4 text-sm text-accent hover:underline"
                  >
                    Réinitialiser les filtres
                  </Button>
                )}
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-card-elevated border-b border-white/8">
                      <tr>
                        <th className="px-6 py-4 text-left">
                          <input
                            type="checkbox"
                            checked={selectAll}
                            onChange={handleSelectAll}
                            className="w-4 h-4 rounded border-white/20 bg-card-elevated text-accent focus:ring-accent"
                          />
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-muted uppercase tracking-wider">Date</th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-muted uppercase tracking-wider">Action</th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-muted uppercase tracking-wider">Table</th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-muted uppercase tracking-wider">Utilisateur</th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-muted uppercase tracking-wider">Email</th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-muted uppercase tracking-wider">Rôle</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/8">
                      {auditLogs?.map((log) => (
                        <tr
                          key={log?.id}
                          className={`hover:bg-card-hover transition-colors ${
                            selectedLogs?.includes(log?.id) ? 'bg-accent/5' : ''
                          }`}
                        >
                          <td className="px-6 py-4">
                            <input
                              type="checkbox"
                              checked={selectedLogs?.includes(log?.id)}
                              onChange={() => handleSelectLog(log?.id)}
                              className="w-4 h-4 rounded border-white/20 bg-card-elevated text-accent focus:ring-accent"
                            />
                          </td>
                          <td className="px-6 py-4 text-sm text-secondary whitespace-nowrap">
                            {formatDate(log?.createdAt)}
                          </td>
                          <td className="px-6 py-4">
                            {getActionBadge(log?.action)}
                          </td>
                          <td className="px-6 py-4 text-sm text-secondary">
                            {log?.tableName || '-'}
                          </td>
                          <td className="px-6 py-4 text-sm text-secondary font-mono text-xs">
                            {log?.userId ? `${log?.userId?.substring(0, 8)}...` : '-'}
                          </td>
                          <td className="px-6 py-4 text-sm text-secondary">
                            {log?.userEmail || '-'}
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              log?.userRole === 'super_admin' ?'bg-accent/10 text-accent border border-accent/30' :'bg-blue-500/10 text-blue-400 border border-blue-500/30'
                            }`}>
                              {log?.userRole || '-'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="p-6 border-t border-white/8 flex items-center justify-between">
                    <p className="text-sm text-secondary">
                      Page {currentPage} sur {totalPages} ({totalCount} logs au total)
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="px-4 py-2 bg-card-elevated hover:bg-card-hover text-secondary rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Icon name="ChevronLeft" size={16} />
                      </Button>
                      <span className="px-4 py-2 bg-accent text-white rounded-lg font-medium">
                        {currentPage}
                      </span>
                      <Button
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="px-4 py-2 bg-card-elevated hover:bg-card-hover text-secondary rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Icon name="ChevronRight" size={16} />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default AdvancedSystemAnalyticsAuditDashboard;